// TypeScript interfaces for ISIS Topology data
import { format, formatDistanceToNowStrict } from 'date-fns';

export interface TopologyLocation {
    city: string;
    country: string;
    label: string;
    datacenterId?: string;
}

export interface Srv6Locator {
    prefix: string;
    algorithm: number;
    endSid: string | null;
    /** Friendly locator name (e.g. "DEFAULT", "LATENCY", "IENTC") injected
     *  by the backend from InfluxDB `srv6_locators.name`. Optional —
     *  fall back to `Algo-${algorithm}` if absent. */
    locatorName?: string;
    /** Owning router's short id (e.g. "mxmex02-rt01"). Optional — used to
     *  disambiguate identical prefixes across multiple owners. */
    ownerRouter?: string;
}

/**
 * Flex-Algo definition serialized per-node from the IS-IS LSDB.
 *
 * - `metricType`: 0 = IGP standard, 1 = min-delay, 2 = TE
 * - `algType`:    0 = SPF,         1 = strict-SPF
 *
 * Currently the backend only emits `flexAlgoCount` — once
 * `parse-isis-lsdb.js:createFrontendOutput` starts serializing the full
 * `flexAlgoDefinitions[]` array (Phase 4 of the flex-algo filter spec),
 * this field becomes populated and SPF-by-flex-algo computation in
 * `utils/pathfinder.ts` can use the right per-link cost.
 *
 * Field stays optional so older topology payloads still type-check.
 */
export interface FlexAlgoDefinition {
    algorithm: number;
    metricType: 0 | 1 | 2;
    algType: 0 | 1;
    priority: number;
    includeAnyExtAdminGroup: string | null;
}

export interface NodePrefix {
    type: string;
    prefix: string;
    metric: number;
    mt: string;
}

export interface TopologyNode {
    id: string;
    hostname: string;
    lspId: string;
    isLocal: boolean;
    areaAddress: string;
    site: string;
    type: 'router' | 'route-reflector';
    role: string;
    location: TopologyLocation;
    srAlgorithms: number[];
    flexAlgoCount: number;
    /** Full flex-algo definitions per node. Populated once the backend
     *  pipeline (`parse-isis-lsdb.js`) starts serializing them. */
    flexAlgoDefinitions?: FlexAlgoDefinition[];
    srv6Locators: Srv6Locator[];
    prefixes: NodePrefix[];
}

export interface LatencyDetail {
    avgDelayUs: number | null;
    minDelayUs: number | null;
    maxDelayUs: number | null;
    delayVariationUs: number | null;
    lossPercent: number | null;
    inboundMbps: number | null;
    outboundMbps: number | null;
}

export interface TopologyLink {
    id: string;
    source: string;
    target: string;
    sourceInterface: string | null;
    sourceInterfaceDesc: string | null;
    targetInterface: string | null;
    targetInterfaceDesc: string | null;
    metric: number;
    forwardMetric?: number;
    reverseMetric?: number;
    bandwidthKbps: number;
    bandwidth: string;
    avgDelayUs: number;
    forward: LatencyDetail;
    reverse: LatencyDetail;
    affinity: string | null;
    isInterSite: boolean;
}

export interface SiteNodeSummary {
    id: string;
    type: 'router' | 'route-reflector';
    role: string;
    isLocal: boolean;
    srv6LocatorCount: number;
    flexAlgoCount: number;
    adjacencyCount: number;
    prefixCount: number;
}

export interface TopologySite {
    id: string;
    location: TopologyLocation;
    nodes: SiteNodeSummary[];
}

export interface InterSiteLink extends TopologyLink {
    sourceSite: string;
    targetSite: string;
}

export interface TopologySummary {
    interSiteLinkCount: number;
    intraSiteLinkCount: number;
    totalNodes: number;
    totalLinks: number;
    totalSites: number;
}

export interface TopologyMetadata {
    source: string;
    timestamp: string;
    command: string;
    instanceName: string;
    level: number;
    totalLspCount: number;
}

/** Algorithm id (string key) → locator display name from ke-topology. */
export type AlgoMap = Record<string, string>;

export interface TopologyData {
    metadata: TopologyMetadata;
    nodes: TopologyNode[];
    links: TopologyLink[];
    topology: {
        sites: TopologySite[];
        interSiteLinks: InterSiteLink[];
        intraSiteLinks: TopologyLink[];
        summary: TopologySummary;
    };
    /** SRv6 flex-algo names keyed by algorithm id, e.g. `"128": "LATENCY"`. */
    algoMap?: AlgoMap;
}

export interface BaselineLink {
    source: string;
    target: string;
    avgDelayUs: number;
    forward: { avgDelayUs: number };
    reverse: { avgDelayUs: number };
    firstSeenAt: string;
    /** Last time this link was observed in baseline telemetry. */
    lastSeenAt?: string;
    /** Human bandwidth label carried over from the topology snapshot. */
    bandwidth?: string;
    /** Whether the link crosses site boundaries (from the topology snapshot). */
    isInterSite?: boolean;
    /** Last-known interface names + descriptions, carried over from the
     *  topology snapshot so a dropped link can still show its port and
     *  carrier (provider). Populated by the backend baseline writer; absent
     *  on older baseline rows, in which case the UI degrades gracefully. */
    sourceInterface?: string | null;
    sourceInterfaceDesc?: string | null;
    targetInterface?: string | null;
    targetInterfaceDesc?: string | null;
}

export interface BaselineData {
    metadata: {
        createdAt: string;
        updatedAt: string;
        version: number;
        linkCount: number;
    };
    links: Record<string, BaselineLink>;
}

/** Baseline rows older than this are hidden across the topology UI. */
export const BASELINE_DISPLAY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Lighter link stroke colors aligned with Reflector `GenericTag` hues
 * (Online / AWS warning / Offline). Mid-tone primitives read softer on
 * the graph than `--color-semantic-text-*` (800 weight). Brand, SPT, and
 * P2P accent colors are intentionally unchanged.
 */
export const TOPOLOGY_LINK_STROKE = {
    success: 'var(--color-primitive-green-600)',
    warning: 'var(--color-primitive-yellow-400)',
    danger: 'var(--color-primitive-red-600)',
    offline: 'var(--color-semantic-text-secondary)',
} as const;

/**
 * Directional latency from TWAMP/telemetry. When both are absent the link
 * is treated as not actively reporting (even if IS-IS still lists it).
 */
export function hasLiveLinkTelemetry(
    link: Pick<TopologyLink, 'forward' | 'reverse'>
): boolean {
    return (
        link.forward?.avgDelayUs != null ||
        link.reverse?.avgDelayUs != null
    );
}

/** Lookup before `filterDisplayableBaseline()` — used for offline detection. */
export function getRawBaselineLink(
    baseline: BaselineData | null | undefined,
    linkId: string
): BaselineLink | null {
    if (!baseline) return null;
    return baseline.links[linkId] ?? null;
}

/**
 * Offline / dropped: IS-IS adjacency remains but directional telemetry is
 * gone and the link was previously tracked in baseline (including rows
 * filtered out for stale `lastSeenAt`).
 */
export function isTopologyLinkOfflineOrDropped(
    link: TopologyLink,
    rawBaseline: BaselineLink | null | undefined
): boolean {
    if (hasLiveLinkTelemetry(link)) return false;
    return rawBaseline != null;
}

/**
 * Default graph/table link stroke — gray when offline/dropped, otherwise
 * latency vs displayable baseline (or absolute fallback).
 */
export function getLinkStrokeColor(
    link: TopologyLink,
    displayableBaselineUs: number | null | undefined,
    rawBaseline: BaselineLink | null | undefined
): string {
    if (isTopologyLinkOfflineOrDropped(link, rawBaseline)) {
        return TOPOLOGY_LINK_STROKE.offline;
    }
    return getLatencyColor(link.avgDelayUs, displayableBaselineUs);
}

/**
 * True when a baseline link is recent enough to show deviation / colour
 * against baseline. Missing, invalid, or stale `lastSeenAt` → false.
 */
export function isBaselineLinkDisplayable(
    link: BaselineLink | null | undefined
): boolean {
    if (!link?.lastSeenAt) return false;
    const seenMs = Date.parse(link.lastSeenAt);
    if (Number.isNaN(seenMs)) return false;
    return Date.now() - seenMs <= BASELINE_DISPLAY_MAX_AGE_MS;
}

/**
 * Drop baseline links whose `lastSeenAt` is missing or older than 7 days.
 * Call once after fetching ke-topology/baseline so every consumer treats
 * stale rows as "no baseline available".
 */
export function filterDisplayableBaseline(data: BaselineData): BaselineData {
    const links: Record<string, BaselineLink> = {};
    for (const [id, link] of Object.entries(data.links ?? {})) {
        if (isBaselineLinkDisplayable(link)) links[id] = link;
    }
    return {
        ...data,
        links,
        metadata: {
            ...data.metadata,
            linkCount: Object.keys(links).length,
        },
    };
}

/**
 * Safe per-link lookup — returns null when the row is absent or stale.
 */
export function getDisplayableBaselineLink(
    baseline: BaselineData | null | undefined,
    linkId: string
): BaselineLink | null {
    if (!baseline) return null;
    const link = baseline.links[linkId];
    return isBaselineLinkDisplayable(link) ? link : null;
}

/**
 * Timing shown for an offline/down link: when it was last seen up
 * (`lastSeenAt`, refreshed by the backend every cycle the adjacency is
 * present) and how long until the baseline row is auto-removed. The removal
 * clock is `lastSeenAt + BASELINE_DISPLAY_MAX_AGE_MS`, which matches both the
 * frontend display cutoff and the backend job's 7-day purge (`STALE_LINK_TTL_MS`),
 * so the countdown reflects when the link actually disappears.
 *
 * Returns null for a missing / unparseable timestamp.
 */
export interface OfflineTiming {
    lastSeenLabel: string;
    lastSeenAgo: string;
    /** ms until auto-removal; <= 0 means it is due to be purged. */
    removesInMs: number;
    removesInLabel: string;
}

export function getOfflineTiming(
    lastSeenAt: string | null | undefined
): OfflineTiming | null {
    if (!lastSeenAt) return null;
    const seenMs = Date.parse(lastSeenAt);
    if (Number.isNaN(seenMs)) return null;

    const removeAtMs = seenMs + BASELINE_DISPLAY_MAX_AGE_MS;
    const removesInMs = removeAtMs - Date.now();
    const seenDate = new Date(seenMs);

    return {
        lastSeenLabel: format(seenDate, 'd MMM yyyy, HH:mm'),
        lastSeenAgo: formatDistanceToNowStrict(seenDate, { addSuffix: true }),
        removesInMs,
        removesInLabel:
            removesInMs <= 0
                ? 'any moment'
                : formatDistanceToNowStrict(new Date(removeAtMs), {
                      addSuffix: true,
                  }),
    };
}

/** All-null latency detail used for synthetic (non-reporting) links. */
const EMPTY_LATENCY_DETAIL: LatencyDetail = {
    avgDelayUs: null,
    minDelayUs: null,
    maxDelayUs: null,
    delayVariationUs: null,
    lossPercent: null,
    inboundMbps: null,
    outboundMbps: null,
};

/**
 * Links tracked in the baseline but absent from the current IS-IS snapshot —
 * i.e. the adjacency dropped entirely (not just its telemetry). These never
 * appear in `topology.json`, so without this the graph and table silently
 * omit them.
 *
 * Returns synthetic `TopologyLink`s rebuilt from the raw baseline so the rest
 * of the UI can render them with the existing offline treatment: they carry no
 * live telemetry (forward/reverse all null), so `isTopologyLinkOfflineOrDropped`
 * classifies them as offline automatically — no new styling required.
 *
 * Guardrails:
 *  - Both endpoints must still exist in the current topology. A fully-down
 *    router/site takes its links with it and is out of scope here (that would
 *    be a separate "node down" feature), so those are skipped.
 *  - Stale baseline rows (>7 days, per `isBaselineLinkDisplayable`) are skipped
 *    so this matches what the rest of the UI already hides.
 *
 * Sorted oldest-`lastSeenAt` first (longest down surfaces first).
 */
export function computeDownLinks(
    rawBaseline: BaselineData | null | undefined,
    topology: TopologyData | null | undefined
): TopologyLink[] {
    if (!rawBaseline || !topology) return [];

    const presentIds = new Set(topology.links.map((l) => l.id));
    const nodeIds = new Set(topology.nodes.map((n) => n.id));

    const down: TopologyLink[] = [];
    for (const [id, bl] of Object.entries(rawBaseline.links)) {
        if (presentIds.has(id)) continue;
        if (!isBaselineLinkDisplayable(bl)) continue;
        if (!nodeIds.has(bl.source) || !nodeIds.has(bl.target)) continue;

        down.push({
            id,
            source: bl.source,
            target: bl.target,
            // Last-known port + description from the baseline (when the
            // backend persists them) so a down link still identifies its
            // interface and carrier/provider. Falls back to null.
            sourceInterface: bl.sourceInterface ?? null,
            sourceInterfaceDesc: bl.sourceInterfaceDesc ?? null,
            targetInterface: bl.targetInterface ?? null,
            targetInterfaceDesc: bl.targetInterfaceDesc ?? null,
            metric: 0,
            bandwidthKbps: 0,
            bandwidth: bl.bandwidth ?? '',
            avgDelayUs: 0,
            forward: { ...EMPTY_LATENCY_DETAIL },
            reverse: { ...EMPTY_LATENCY_DETAIL },
            affinity: null,
            isInterSite: bl.isInterSite ?? false,
        });
    }

    down.sort((a, b) => {
        const ta = Date.parse(rawBaseline.links[a.id]?.lastSeenAt ?? '') || 0;
        const tb = Date.parse(rawBaseline.links[b.id]?.lastSeenAt ?? '') || 0;
        return ta - tb;
    });
    return down;
}

// Path Analysis types

export interface PathHop {
    from: string;
    to: string;
    linkId: string;
    forwardLatencyUs: number;
    reverseLatencyUs: number;
    rttUs: number;
    bandwidth: string;
    bandwidthKbps: number;
    carrier: string | null;
    lossPercent: number | null;
}

export interface LatencyPath {
    hops: PathHop[];
    forwardLatencyUs: number;
    reverseLatencyUs: number;
    rttUs: number;
    hopCount: number;
    isBest: boolean;
    isDirect: boolean;
    bottleneckBandwidth: string;
    bottleneckBandwidthKbps: number;
}

// Site-to-Site Path Analysis types

export interface SiteLatencyPath extends LatencyPath {
    sourceRouter: string;
    targetRouter: string;
}

export interface SiteLatencyResult {
    sourceSite: string;
    targetSite: string;
    sourceLocation: TopologyLocation;
    targetLocation: TopologyLocation;
    totalPaths: number;
    bestRttUs: number;
    worstRttUs: number;
    avgRttUs: number;
    paths: SiteLatencyPath[];
}

// Helpers

/**
 * Normalize a topology hostname to the SRv6 short format.
 * "KE.MXQRO01.RT01" -> "mxqro01-rt01"
 * "KE.MXQRO01.RT01.ctx.ke" -> "mxqro01-rt01"
 * Already-short names pass through unchanged (lowercased).
 */
export function toShortHostname(hostname: string): string {
    const clean = hostname.split(':')[0];
    const parts = clean.split('.');
    if (parts.length >= 3) {
        return `${parts[1]}-${parts[2]}`.toLowerCase();
    }
    return hostname.toLowerCase();
}

export function usToMs(us: number | null): string {
    if (us === null || us === undefined) return '-';
    if (us < 1000) return `${us} μs`;
    return `${(us / 1000).toFixed(1)} ms`;
}

/**
 * Returns latency color based on deviation from baseline.
 * - Green: current <= baseline (at or below expected)
 * - Yellow: current is up to 30% above baseline
 * - Red: current is more than 30% above baseline
 *
 * Falls back to absolute thresholds when no baseline is available.
 */
export function getLatencyColor(
    avgDelayUs: number,
    baselineUs?: number | null
): string {
    if (baselineUs != null && baselineUs > 0) {
        const ratio = avgDelayUs / baselineUs;
        if (ratio <= 1.0) return TOPOLOGY_LINK_STROKE.success;
        if (ratio <= 1.3) return TOPOLOGY_LINK_STROKE.warning;
        return TOPOLOGY_LINK_STROKE.danger;
    }
    // Fallback: absolute thresholds (no baseline available)
    if (avgDelayUs < 1000) return TOPOLOGY_LINK_STROKE.success;
    if (avgDelayUs < 5000) return TOPOLOGY_LINK_STROKE.warning;
    return TOPOLOGY_LINK_STROKE.danger;
}

/**
 * Returns latency severity level based on baseline deviation.
 */
export function getLatencyLevel(
    avgDelayUs: number,
    baselineUs?: number | null
): 'normal' | 'warning' | 'critical' {
    if (baselineUs != null && baselineUs > 0) {
        const ratio = avgDelayUs / baselineUs;
        if (ratio <= 1.0) return 'normal';
        if (ratio <= 1.3) return 'warning';
        return 'critical';
    }
    if (avgDelayUs < 1000) return 'normal';
    if (avgDelayUs < 5000) return 'warning';
    return 'critical';
}

/**
 * Returns the % deviation from baseline, e.g. "+12.5%" or "-3.2%"
 */
export function getBaselineDeviation(
    avgDelayUs: number,
    baselineUs: number
): string {
    if (baselineUs <= 0) return '-';
    const pct = ((avgDelayUs - baselineUs) / baselineUs) * 100;
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
}

/**
 * Format loss percentage for display.
 * Returns "0%" for zero loss, "0.12%" for fractional, or "-" for null.
 */
export function formatLoss(lossPercent: number | null | undefined): string {
    if (lossPercent == null) return '-';
    if (lossPercent === 0) return '0%';
    if (lossPercent < 0.01) return '<0.01%';
    return `${lossPercent.toFixed(2)}%`;
}

/**
 * Returns the colour for the Packet Loss overlay (the per-link line colour
 * shown when the user toggles the "Packet Loss" overlay button).
 *
 * Three-tier severity:
 *   - Green  (success): null or loss ≤ 0.1%
 *   - Yellow (warning): 0.1% < loss ≤ 1%
 *   - Red    (danger):  loss > 1%
 *
 * The legend swatches in TopologyGraph use the same three semantic tokens,
 * so the line colour and the legend label always agree. Adjust the two
 * thresholds here to retune both at once.
 */
export function getPacketLossOverlayColor(
    lossPercent: number | null | undefined
): string {
    if (lossPercent == null || lossPercent <= 0.1)
        return TOPOLOGY_LINK_STROKE.success;
    if (lossPercent <= 1) return TOPOLOGY_LINK_STROKE.warning;
    return TOPOLOGY_LINK_STROKE.danger;
}

/**
 * Returns color for packet loss severity.
 * - Green: 0% loss
 * - Yellow: > 0% and <= 0.1%
 * - Red: > 0.1%
 */
export function getLossColor(
    lossPercent: number | null | undefined
): string {
    if (lossPercent == null || lossPercent === 0)
        return TOPOLOGY_LINK_STROKE.success;
    if (lossPercent <= 0.1) return TOPOLOGY_LINK_STROKE.warning;
    return TOPOLOGY_LINK_STROKE.danger;
}

/**
 * Format Mbps value for display.
 * Returns "1.23 Mbps", "0.45 Gbps", or "-" for null.
 */
export function formatMbps(mbps: number | null | undefined): string {
    if (mbps == null) return '-';
    if (mbps >= 1000) return `${(mbps / 1000).toFixed(2)} Gbps`;
    if (mbps >= 1) return `${mbps.toFixed(2)} Mbps`;
    if (mbps >= 0.001) return `${(mbps * 1000).toFixed(1)} Kbps`;
    return '0 Mbps';
}

// --- Link card field visibility filter ---

export interface LinkLabelFields {
    latency: boolean;
    rtt: boolean;
    baseline: boolean;
    isisMetric: boolean;
    bandwidth: boolean;
    carrier: boolean;
    throughput: boolean;
    utilization: boolean;
    loss: boolean;
}

export const DEFAULT_LINK_LABEL_FIELDS: LinkLabelFields = {
    latency: true,
    rtt: false,
    baseline: false,
    isisMetric: false,
    bandwidth: true,
    carrier: false,
    throughput: false,
    utilization: false,
    loss: false,
};

export const LINK_LABEL_FIELD_NAMES: Record<keyof LinkLabelFields, string> = {
    latency: 'Latency',
    rtt: 'RTT',
    baseline: 'Baseline deviation',
    isisMetric: 'IS-IS Metric',
    bandwidth: 'Capacity',
    carrier: 'Carrier',
    throughput: 'Throughput (Rx/Tx)',
    utilization: 'Network share (%)',
    loss: 'Packet loss',
};

/**
 * Returns color for ISIS metric cost. Low cost = green, mid = yellow, high = red.
 * Thresholds tuned for typical ISIS wide metrics (10 = directly connected, 100+ = high).
 */
export function getMetricColor(metric: number): string {
    if (metric <= 10) return TOPOLOGY_LINK_STROKE.success;
    if (metric <= 50) return TOPOLOGY_LINK_STROKE.warning;
    return TOPOLOGY_LINK_STROKE.danger;
}

/**
 * Returns color for link utilization (traffic / capacity).
 * Green: < 30%, Yellow: 30-70%, Red: > 70%.
 * Returns secondary color when utilization is unknown.
 */
export function getUtilizationColor(utilizationPct: number | null): string {
    if (utilizationPct == null) return 'var(--color-semantic-text-secondary)';
    if (utilizationPct < 30) return TOPOLOGY_LINK_STROKE.success;
    if (utilizationPct <= 70) return TOPOLOGY_LINK_STROKE.warning;
    return TOPOLOGY_LINK_STROKE.danger;
}

// SPT (Shortest Path Tree) types

export interface SptNode {
    id: string;
    cost: number;
    parent: string | null;
    parentLinkId: string | null;
}

export interface SptResult {
    root: string;
    nodes: Record<string, SptNode>;
    treeEdgeIds: Set<string>;
}

export function getBandwidthStroke(bandwidth: string): number {
    if (bandwidth.includes('100')) return 4;
    if (bandwidth.includes('10')) return 2.5;
    return 1.5;
}

export function shortBandwidth(bandwidth: string): string {
    return bandwidth
        .replace(' Gbps', 'G')
        .replace(' Mbps', 'M')
        .replace(' Kbps', 'K');
}

/** Bandwidth parsed from an interface description `CAP_*` token. */
export interface ResolvedLinkBandwidth {
    /** Full label, e.g. `"700 Mbps"` or `"100 Gbps"`. */
    label: string;
    /** Compact graph label, e.g. `"700M"` or `"100G"`. */
    shortLabel: string;
    /** Numeric capacity in kbps (for sort / utilization). */
    kbps: number;
    /** True when a description `CAP_*` token drove this value (not IS-IS only). */
    fromDescription?: boolean;
}

interface ParsedCapBandwidth extends ResolvedLinkBandwidth {
    /** Unit letter from the CAP token (`M` = Mbps, `G` = Gbps). */
    capUnit: 'M' | 'G';
    /** Raw CAP token from the description, e.g. `CAP_700M`. */
    capToken: string;
}

function bandwidthFromApiFields(
    link: Pick<TopologyLink, 'bandwidth' | 'bandwidthKbps'>
): ResolvedLinkBandwidth {
    return {
        label: link.bandwidth,
        shortLabel: shortBandwidth(link.bandwidth),
        kbps: link.bandwidthKbps,
    };
}

function buildParsedCapBandwidth(
    value: number,
    capUnit: 'M' | 'G',
    capToken: string
): ParsedCapBandwidth {
    if (capUnit === 'G') {
        const label = `${value} Gbps`;
        return {
            label,
            shortLabel: shortBandwidth(label),
            kbps: value * 1_000_000,
            capUnit,
            capToken,
        };
    }

    const label = `${value} Mbps`;
    return {
        label,
        shortLabel: shortBandwidth(label),
        kbps: value * 1_000,
        capUnit,
        capToken,
    };
}

const CAP_TOKEN_RE = /CAP_(\d+(?:\.\d+)?)([MG])/gi;

/**
 * Parse every `CAP_*` token from one interface description.
 * Handles piped (`BB|CAP_100G|DEST_...`) and concatenated
 * (`BBICAP_700MIDEST_...`) formats.
 */
export function parseAllCapBandwidthsFromDescription(
    description: string | null | undefined
): ParsedCapBandwidth[] {
    if (!description) return [];

    const seen = new Set<string>();
    const results: ParsedCapBandwidth[] = [];
    const re = new RegExp(CAP_TOKEN_RE.source, CAP_TOKEN_RE.flags);

    let match: RegExpExecArray | null;
    while ((match = re.exec(description)) !== null) {
        const value = parseFloat(match[1]);
        if (!Number.isFinite(value) || value <= 0) continue;

        const capUnit = match[2].toUpperCase() as 'M' | 'G';
        const capToken = match[0].toUpperCase();
        const key = capToken;
        if (seen.has(key)) continue;
        seen.add(key);

        results.push(buildParsedCapBandwidth(value, capUnit, capToken));
    }

    return results;
}

/**
 * Parse the first `CAP_*` token from a description (convenience wrapper).
 */
export function parseBandwidthFromDescription(
    description: string | null | undefined
): ParsedCapBandwidth | null {
    return parseAllCapBandwidthsFromDescription(description)[0] ?? null;
}

/**
 * Collect unique `CAP_*` tokens from every interface description on a link.
 */
export function collectLinkCapBandwidths(
    link: Pick<TopologyLink, 'sourceInterfaceDesc' | 'targetInterfaceDesc'>
): ParsedCapBandwidth[] {
    const seen = new Set<string>();
    const results: ParsedCapBandwidth[] = [];

    for (const desc of [link.sourceInterfaceDesc, link.targetInterfaceDesc]) {
        for (const parsed of parseAllCapBandwidthsFromDescription(desc)) {
            const key = `${parsed.capToken}|${parsed.kbps}`;
            if (seen.has(key)) continue;
            seen.add(key);
            results.push(parsed);
        }
    }

    return results;
}

function toResolvedBandwidth(
    parsed: ParsedCapBandwidth,
    fromDescription = true
): ResolvedLinkBandwidth {
    return {
        label: parsed.label,
        shortLabel: parsed.shortLabel,
        kbps: parsed.kbps,
        fromDescription,
    };
}

/**
 * Pick the CAP token that represents link capacity using description
 * nomenclature only (`M` → Mbps, `G` → Gbps). Prefers explicit Mbps
 * commits when present; otherwise uses the first collected token
 * (source description before target).
 */
function pickFromCapCandidates(
    candidates: ParsedCapBandwidth[]
): ParsedCapBandwidth | null {
    if (candidates.length === 0) return null;

    const mCaps = candidates.filter((c) => c.capUnit === 'M');
    if (mCaps.length > 0) {
        return mCaps.reduce((best, cur) =>
            cur.kbps < best.kbps ? cur : best
        );
    }

    return candidates[0];
}

/**
 * Resolve the bandwidth shown across the topology UI.
 *
 * Scans both endpoint descriptions for `CAP_*` tokens and applies
 * nomenclature directly — `M` suffix → Mbps, `G` suffix → Gbps.
 * Falls back to ke-topology `bandwidth` / `bandwidthKbps` when no
 * CAP token is present in either description.
 */
export function resolveLinkBandwidth(
    link: Pick<
        TopologyLink,
        'bandwidth' | 'bandwidthKbps' | 'sourceInterfaceDesc' | 'targetInterfaceDesc'
    >
): ResolvedLinkBandwidth {
    const api = bandwidthFromApiFields(link);
    const picked = pickFromCapCandidates(collectLinkCapBandwidths(link));
    if (!picked) {
        return { ...api, fromDescription: false };
    }
    return toResolvedBandwidth(picked);
}

// --- Flex-Algo filter ---

/**
 * Known flex-algo ids → locator names. Used only when ke-topology does
 * not include `algoMap` — backend values always win when present.
 */
export const DEFAULT_FLEX_ALGO_MAP: AlgoMap = {
    '0': 'DEFAULT',
    '128': 'LATENCY',
    '130': 'IENTC',
    '131': 'CC',
    '132': 'ARELION',
    '133': 'C3NTRO',
    '134': 'NO_IENTC',
    '135': 'IENTC_STRICT',
    '136': 'ARELION_STRICT',
    '137': 'CC_STRICT',
    '138': 'C3NTRO_STRICT',
};

/**
 * Resolve the human-readable flex-algo name for display.
 * Priority: ke-topology `algoMap` → `DEFAULT_FLEX_ALGO_MAP` (only if
 * backend omitted `algoMap`) → per-locator `locatorName` → `Algo-N`.
 */
export function resolveAlgoLocatorName(
    algorithm: number,
    algoMap?: AlgoMap | null,
    nodeLocatorName?: string | null
): string {
    const key = String(algorithm);
    const fromBackend = algoMap?.[key]?.trim();
    if (fromBackend) return fromBackend;

    if (algoMap == null) {
        const fromFallback = DEFAULT_FLEX_ALGO_MAP[key]?.trim();
        if (fromFallback) return fromFallback;
    }

    const fromNode = nodeLocatorName?.trim();
    if (fromNode) return fromNode;
    return `Algo-${algorithm}`;
}

/**
 * Aggregated flex-algo entry shown in the toolbar dropdown. Built by
 * `listAvailableAlgos` from the per-node `srv6Locators[]`.
 */
export interface AvailableAlgo {
    algorithm: number;
    /** Display name (e.g. "LATENCY") — from backend `algoMap`, frontend
     *  fallback, or node locators. */
    locatorName: string;
    /** How many nodes have at least one srv6Locator for this algorithm. */
    nodeCount: number;
}

/**
 * Walk every node's srv6Locators and group by `algorithm`, returning a
 * sorted, deduplicated list suitable for a single-select dropdown.
 *
 * Stable key: `algorithm` (number). The label `locatorName` is
 * presentational only — the rest of the filter pipeline keys off the
 * algorithm number.
 */
export function listAvailableAlgos(data: TopologyData): AvailableAlgo[] {
    const map = new Map<number, AvailableAlgo>();
    const seenPerAlgo = new Map<number, Set<string>>();

    for (const node of data.nodes) {
        const seenAlgosForNode = new Set<number>();
        for (const loc of node.srv6Locators) {
            const algo = loc.algorithm;
            const existing = map.get(algo);
            const friendly = resolveAlgoLocatorName(
                algo,
                data.algoMap,
                loc.locatorName
            );

            if (!existing) {
                map.set(algo, {
                    algorithm: algo,
                    locatorName: friendly,
                    nodeCount: 0,
                });
            } else if (
                friendly !== `Algo-${algo}` &&
                existing.locatorName === `Algo-${algo}`
            ) {
                existing.locatorName = friendly;
            }

            if (!seenAlgosForNode.has(algo)) {
                seenAlgosForNode.add(algo);
                const nodes = seenPerAlgo.get(algo) ?? new Set<string>();
                nodes.add(node.id);
                seenPerAlgo.set(algo, nodes);
            }
        }
    }

    for (const [algo, nodes] of seenPerAlgo.entries()) {
        const entry = map.get(algo);
        if (entry) entry.nodeCount = nodes.size;
    }

    return Array.from(map.values()).sort(
        (a, b) => a.algorithm - b.algorithm
    );
}

/**
 * Format an algorithm for the dropdown / badges.
 * "0 (DEFAULT)", "128 (LATENCY)", or "130 (Algo-130)" when unmapped.
 */
export function formatAlgoLabel(a: AvailableAlgo): string {
    return `${a.algorithm} (${a.locatorName})`;
}

// --- Flex-algo traffic links (real traffic — modo 2A) ---

/**
 * Normalized link row for topology join — built from DDM demands or
 * (legacy) `GET /srv6-insight/links-by-algo`. Keys are the SRv6 source
 * router (short hostname) plus the egress interface.
 */
export interface LinksByAlgoLink {
    /** Short hostname, e.g. "mxmex02-rt01". */
    source: string;
    sourceFqdn?: string | null;
    /** Cisco-style egress interface, e.g. "HundredGigE0/0/0/0". */
    interface: string;
    /** Resolved IS-IS neighbor short hostname when available. */
    neighbor?: string | null;
    mbps?: number | null;
    pps?: number | null;
}

export interface LinksByAlgoResponse {
    algorithm: number;
    locatorName: string | null;
    timeRange: string;
    links: LinksByAlgoLink[];
}

/** Minimal DDM demand row used to derive traffic link highlights. */
export interface DdmDemandRow {
    source: string;
    dest: string;
    algo: string;
    interfaceOut: string;
    mbps?: number;
    pps?: number;
}

/**
 * Collapse DDM demands into deduplicated `(source, interface)` link rows
 * for the flex-algo traffic overlay. Backend filters by `algo=` but we
 * also match client-side when the response is unfiltered.
 */
export function linksByAlgoFromDdmDemands(
    algorithm: number,
    locatorName: string,
    timeRange: string,
    demands: DdmDemandRow[]
): LinksByAlgoResponse {
    const algoKey = locatorName.trim().toLowerCase();
    const seen = new Set<string>();
    const links: LinksByAlgoLink[] = [];

    for (const d of demands) {
        if (d.algo.trim().toLowerCase() !== algoKey) continue;
        const dedupeKey = `${d.source.trim().toLowerCase()}|${d.interfaceOut.trim().toLowerCase()}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        links.push({
            source: d.source,
            interface: d.interfaceOut,
            neighbor: d.dest,
            mbps: d.mbps ?? null,
            pps: d.pps ?? null,
        });
    }

    return {
        algorithm,
        locatorName,
        timeRange,
        links,
    };
}

/**
 * Mode toggling between the two ways to highlight links for a flex-algo:
 *  - `off`     — just dim non-participating nodes (Level 1 only).
 *  - `traffic` — color links currently carrying real traffic for the algo.
 *  - `spf`     — color links the algorithm's SPF would compute.
 *  - `both`    — overlay both sets (traffic accent + SPF dashed).
 */
export type AlgoLinkMode = 'off' | 'traffic' | 'spf' | 'both';

/** Flex-Algo link colours — shared by LinkEdge and graph legend. */
export const FLEX_ALGO_TRAFFIC_COLOR = TOPOLOGY_LINK_STROKE.success;
/** SPF-only edges when Traffic + SPF are both active (`both` mode). */
export const FLEX_ALGO_SPF_BOTH_COLOR =
    'var(--color-semantic-text-brand)';
export const FLEX_ALGO_DIM_COLOR =
    'var(--color-semantic-text-secondary)';

/**
 * Resolve flex-algo edge stroke colour. In `both` mode, live traffic
 * stays green (solid) and computed SPF-only paths use brand/turquoise
 * (dashed) so the two layers are distinguishable at a glance.
 */
export function flexAlgoLinkColor(
    mode: AlgoLinkMode,
    trafficLink: boolean,
    spfLink: boolean,
    highlighted: boolean
): string {
    if (!highlighted) return FLEX_ALGO_DIM_COLOR;
    if (mode === 'both' && spfLink && !trafficLink) {
        return FLEX_ALGO_SPF_BOTH_COLOR;
    }
    return FLEX_ALGO_TRAFFIC_COLOR;
}

/**
 * Extract carrier name from interface description.
 * Descriptions follow the pattern: BB|CAP_100G|DEST_...|CARR_IENTC|OID_...|SR_...
 * Capacity is parsed separately — see `parseBandwidthFromDescription`.
 * Returns the carrier name (e.g. "IENTC") or null if not found.
 */
export function parseCarrier(
    description: string | null | undefined
): string | null {
    if (!description) return null;
    const match = description.match(/CARR_([^|]+)/);
    return match ? match[1] : null;
}
